import React, { useEffect, useState } from 'react';
import { Typography, Button as MuiButton, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { useMain } from 'src/views/hooks/useMain';
import CustomShowLoader from 'src/components/CustomComponents/CustomShowLoader';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material';
import { Button } from 'reactstrap';
import TextInputs from 'src/components/inputs/TextInputs';

const useStyles = makeStyles({
  container: {
    padding: '20px',
    backgroundColor: '#FAFAFA',
    borderRadius: '10px',
    border: '0.5px solid #CFCFCF',
  },
  linkText: {
    wordWrap: 'break-word',
    cursor: 'pointer',
    textDecoration: 'underline',
    maxWidth: '50%',
    display: 'inline-block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const WebAppointmentBookingLink = ({ data, refreshData }) => {
  const { setShowMessage, createWebAppointmentBookingLink, activeBranchId, updateShowServicePriceInWebsite } = useMain();

  const classes = useStyles();
  const [bookingLink, setBookingLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showServicePriceInWebsite, setShowServicePriceInWebsite] = useState(data?.showServicePriceInWebsite || false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openDialogue, setOpenDialogue] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setShowServicePriceInWebsite(data?.showServicePriceInWebsite || false);
  }, [data?.showServicePriceInWebsite]);

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const res = await createWebAppointmentBookingLink(activeBranchId);
      if (res.statusCode === 200) {
        setBookingLink(res.data);
      } else {
        setShowMessage({ message: res.message || 'Failed to generate link', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(bookingLink);
      setShowMessage({ message: 'Copied', messageType: 'success' });
    } catch (err) {
      setShowMessage({ message: err.message || 'Failed to copy', messageType: 'error' });
    }
  };

  const handleToggleChange = () => {
    setOpenDialogue(true);
  };

  const handleCloseDialogue = () => {
    setOpenDialogue(false);
    setPassword('');
    setShowPassword(false);
  };

  const handleUpdateShowServicePrice = async () => {
    if (!password) {
      setShowMessage({ message: 'Please enter login password', messageType: 'error' });
      return;
    }
    setIsSubmitting(true);
    try {
      // Note: The password is not sent to the API, as the API does not support it.
      // This is a simple client-side check as requested.
      const res = await updateShowServicePriceInWebsite(data?._id);

      if (res.statusCode === 200) {
        setShowMessage({ message: res.message || 'Successfully updated settings', messageType: 'success' });
        setShowServicePriceInWebsite(!showServicePriceInWebsite);
        setOpenDialogue(false);
        setPassword('');
        setShowPassword(false);
        refreshData(data?._id);
      } else {
        setShowMessage({ message: res.message || 'Failed to update settings', messageType: 'error' });
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      setBookingLink(null);
    };
  }, [activeBranchId]);

  return (
    <div className={classes.container}>
      <Typography variant="body1">Web Appointment Booking Link</Typography>
      {bookingLink ? (
        <Box display="flex" alignItems="center" mt={2}>
          <a
            href={bookingLink}
            className={classes.linkText}
            target="_blank"
            rel="noopener noreferrer"
          >
            {bookingLink}
          </a>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={copyToClipboard}
            sx={{ marginLeft: '10px' }}
          >
            Copy Link
          </MuiButton>
        </Box>
      ) : (
        <Box mt={2}>
          {loading ? (
            <CustomShowLoader size={24} color={'#4caf50'} />
          ) : (
            <MuiButton variant="contained" color="primary" onClick={handleGenerateLink}>
              Generate Link
            </MuiButton>
          )}
        </Box>
      )}

      <Box mt={3}>
        <FormControlLabel
          control={<Switch checked={showServicePriceInWebsite} onChange={handleToggleChange} />}
          label="Show Service Price In Website"
        />
      </Box>

      <Dialog open={openDialogue} onClose={handleCloseDialogue}>
        <DialogTitle>Confirm Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to turn "Show Service Price In Website" {showServicePriceInWebsite ? 'OFF' : 'ON'}?
          </DialogContentText>
          <Box style={{ marginTop: '10px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextInputs
                  title="Login Password"
                  name="password"
                  value={password}
                  handleChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  disabled={isSubmitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          disabled={isSubmitting}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogue}>Cancel</Button>
          <Button onClick={handleUpdateShowServicePrice} autoFocus disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WebAppointmentBookingLink;
